import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users,
  Copy,
  TrendingUp,
  Trophy,
  Settings2,
  AlertCircle,
  Plus,
  X,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Trader {
  wallet_address: string;
  total_profit: number;
  total_volume: number;
  win_rate: number;
  rank: number;
}

export function CopyTrading() {
  const { publicKey, connected } = useWallet();
  const queryClient = useQueryClient();
  const [searchWallet, setSearchWallet] = useState("");
  const [copyAmount, setCopyAmount] = useState([0.5]); // Default 0.5 SOL per trade
  const [maxDaily, setMaxDaily] = useState([5]); // Max 5 SOL per day
  const [autoCopy, setAutoCopy] = useState(false);
  const [copiedTraders, setCopiedTraders] = useState<string[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Fetch top traders
  const { data: topTraders, isLoading } = useQuery({
    queryKey: ["top-traders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leaderboard_entries")
        .select("*")
        .order("total_profit", { ascending: false })
        .limit(20);

      if (error) throw error;
      return data as Trader[];
    },
  });

  const addCopyTrader = (wallet: string) => {
    if (copiedTraders.includes(wallet)) {
      toast({ title: "Already copying this trader", variant: "destructive" });
      return;
    }
    setCopiedTraders(prev => [...prev, wallet]);
    toast({
      title: "Now copying trader",
      description: `You'll mirror trades from ${wallet.slice(0, 6)}...${wallet.slice(-4)}`,
    });
  };

  const removeCopyTrader = (wallet: string) => {
    setCopiedTraders(prev => prev.filter(w => w !== wallet));
    toast({ title: "Stopped copying trader" });
  };

  if (!connected) {
    return (
      <Card className="p-6 text-center">
        <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="font-semibold mb-2">Copy Trading</h3>
        <p className="text-sm text-muted-foreground">
          Connect your wallet to copy successful traders
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Copy Trading Settings */}
      <Card>
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Copy Trading Settings</h3>
          </div>
          <Switch
            checked={autoCopy}
            onCheckedChange={(checked) => {
              if (checked) {
                setShowConfirmDialog(true);
              } else {
                setAutoCopy(false);
              }
            }}
          />
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Amount per trade: {copyAmount[0]} SOL
            </label>
            <Slider
              value={copyAmount}
              onValueChange={setCopyAmount}
              min={0.1}
              max={5}
              step={0.1}
            />
          </div>

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Max daily spend: {maxDaily[0]} SOL
            </label>
            <Slider
              value={maxDaily}
              onValueChange={setMaxDaily}
              min={1}
              max={50}
              step={1}
            />
          </div>

          <div className="p-3 rounded-lg bg-warning/10 border border-warning/20 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
            <p className="text-xs text-warning">
              Copy trading involves risk. Past performance doesn't guarantee future results.
              Only invest what you can afford to lose.
            </p>
          </div>
        </div>
      </Card>

      {/* Copied Traders */}
      {copiedTraders.length > 0 && (
        <Card>
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2">
              <Copy className="w-5 h-5 text-primary" />
              Traders You're Copying ({copiedTraders.length})
            </h3>
          </div>
          <div className="divide-y divide-border">
            {copiedTraders.map((wallet) => (
              <div key={wallet} className="p-3 flex items-center justify-between">
                <div className="font-mono text-sm">
                  {wallet.slice(0, 8)}...{wallet.slice(-6)}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeCopyTrader(wallet)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Add Trader by Wallet */}
      <Card className="p-4">
        <label className="text-sm text-muted-foreground mb-2 block">
          Copy trader by wallet address
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Enter wallet address..."
              value={searchWallet}
              onChange={(e) => setSearchWallet(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            onClick={() => {
              if (searchWallet.length > 30) {
                addCopyTrader(searchWallet);
                setSearchWallet("");
              } else {
                toast({ title: "Invalid wallet address", variant: "destructive" });
              }
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </Card>

      {/* Top Traders Leaderboard */}
      <Card>
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            <h3 className="font-semibold">Top Traders</h3>
          </div>
          <Badge variant="secondary">Last 30 days</Badge>
        </div>

        <div className="divide-y divide-border">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="p-3 flex items-center gap-3">
                <Skeleton className="w-8 h-6" />
                <div className="flex-1 min-w-0">
                  <Skeleton className="h-4 w-32 mb-1" />
                  <div className="flex items-center gap-3 mt-1">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-3 w-12" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <Skeleton className="h-8 w-16" />
              </div>
            ))
          ) : topTraders && topTraders.length > 0 ? (
            topTraders.map((trader, index) => {
              const isCopied = copiedTraders.includes(trader.wallet_address);

              return (
                <div
                  key={trader.wallet_address}
                  className={cn(
                    "p-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors",
                    isCopied && "bg-primary/5"
                  )}
                >
                  {/* Rank */}
                  <div className="w-8 text-center">
                    {index === 0 ? (
                      <span className="text-xl">🥇</span>
                    ) : index === 1 ? (
                      <span className="text-xl">🥈</span>
                    ) : index === 2 ? (
                      <span className="text-xl">🥉</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">#{index + 1}</span>
                    )}
                  </div>

                  {/* Trader Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm truncate">
                      {trader.wallet_address.slice(0, 6)}...{trader.wallet_address.slice(-4)}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-success" />
                        {trader.total_profit?.toFixed(2) || 0} SOL
                      </span>
                      <span>Win: {trader.win_rate?.toFixed(0) || 0}%</span>
                      <span>Vol: {trader.total_volume?.toFixed(0) || 0} SOL</span>
                    </div>
                  </div>

                  {/* Copy Button */}
                  <Button
                    variant={isCopied ? "secondary" : "default"}
                    size="sm"
                    onClick={() =>
                      isCopied
                        ? removeCopyTrader(trader.wallet_address)
                        : addCopyTrader(trader.wallet_address)
                    }
                  >
                    {isCopied ? (
                      <>
                        <X className="w-3 h-3 mr-1" />
                        Stop
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                <Trophy className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No Traders Yet</h3>
              <p className="text-muted-foreground max-w-sm">
                Start trading to appear on the leaderboard and get copied by others.
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enable Auto Copy Trading</DialogTitle>
            <DialogDescription>
              This will automatically copy trades from traders you follow. Make sure you understand the risks involved.
              Copy trading can result in significant losses if the traders you follow make poor decisions.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setAutoCopy(true);
                setShowConfirmDialog(false);
                toast({
                  title: "Auto Copy Trading Enabled",
                  description: "You will now automatically copy trades from followed traders",
                });
              }}
            >
              Enable Auto Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
